import type { IInputDatasetResponse } from '@escloud/types';
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkPiecewiseFunction from '@kitware/vtk.js/Common/DataModel/PiecewiseFunction';
import vtkInteractorStyleImage from '@kitware/vtk.js/Interaction/Style/InteractorStyleImage';
import vtkInteractorStyleTrackballCamera from '@kitware/vtk.js/Interaction/Style/InteractorStyleTrackballCamera';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import vtkImageMapper from '@kitware/vtk.js/Rendering/Core/ImageMapper';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
import vtkVolume from '@kitware/vtk.js/Rendering/Core/Volume';
import vtkVolumeMapper from '@kitware/vtk.js/Rendering/Core/VolumeMapper';
// Importing the OpenGL backend registers the 'WebGL' view constructor
// and exposes vtkOpenGLRenderWindow.newInstance() for direct use.
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Slider } from '../ui/slider';

import { useDatasetPreview } from '@/hooks/use-datasets';
import { formatDicomDate, formatDicomName } from '@/lib/dicom';
import { errorIds, logError } from '@/lib/log-error';
import { cn } from '@/lib/utils';
import { parseMha } from '@/visualization/utils/mha-parser';

interface Props {
    dataset: IInputDatasetResponse | null;
}

/**
 * Tabbed MPR + 3D preview for the wizard Imaging step.
 *
 * One persistent VTK pipeline lives for the lifetime of the component:
 * a single `vtkRenderWindow` + `vtkOpenGLRenderWindow` + renderer +
 * interactor, plus three pre-built slice actors (one per axis) and a
 * volume actor with CT transfer functions. Only one pane is visible
 * at a time, so a shared multi-view WebGL context buys nothing here
 * — a single render window keeps the lifecycle trivial.
 *
 * Tab switch swaps which actor is in the renderer, the interactor
 * style, and the camera config. Dataset switch updates the mappers'
 * inputs and re-fits the active camera. Disposal happens exactly
 * once on unmount.
 *
 * Like the previous viewer this is intentionally NOT routed through
 * `useVisualization()`; the wizard has no case-scoped
 * `VisualizationProvider`. The pipeline owns its own private VTK
 * objects.
 */
export function DatasetMultiPaneViewer({ dataset }: Props) {
    const { data, isLoading, isError, error } = useDatasetPreview(dataset?.id ?? undefined);

    if (!dataset) {
        return <Placeholder>Select a dataset to preview</Placeholder>;
    }
    if (isLoading) {
        return (
            <Placeholder>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading preview…
            </Placeholder>
        );
    }
    if (isError) {
        const status = (error as { status?: number } | null)?.status;
        return (
            <Placeholder>
                <AlertCircle className="mr-2 h-4 w-4 text-amber-500" />
                {status === 404 ? 'No preview available' : 'Failed to load preview'}
            </Placeholder>
        );
    }
    if (!data) {
        return <Placeholder>No preview available</Placeholder>;
    }

    return <MultiPaneRenderer dataset={dataset} arrayBuffer={data} />;
}

function Placeholder({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex h-full w-full items-center justify-center rounded-md border bg-muted/30 text-sm text-muted-foreground">
            {children}
        </div>
    );
}

type PreviewTab = 'axial' | 'coronal' | 'sagittal' | '3d';
type SliceAxis = 'K' | 'J' | 'I';

const TABS: ReadonlyArray<{ id: PreviewTab; label: string }> = [
    { id: 'axial', label: 'Axial' },
    { id: 'coronal', label: 'Coronal' },
    { id: 'sagittal', label: 'Sagittal' },
    { id: '3d', label: '3D' },
];

const AXIS_FOR_TAB: Record<Exclude<PreviewTab, '3d'>, SliceAxis> = {
    axial: 'K',
    coronal: 'J',
    sagittal: 'I',
};

interface SliceState {
    /** Current 0-based slice index per axis. */
    index: Record<SliceAxis, number>;
    /** Total slice count per axis, derived from the volume dimensions. */
    count: Record<SliceAxis, number>;
}

function MultiPaneRenderer({
    dataset,
    arrayBuffer,
}: {
    dataset: IInputDatasetResponse;
    arrayBuffer: ArrayBuffer;
}) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const pipelineRef = useRef<Pipeline | null>(null);

    const [activeTab, setActiveTab] = useState<PreviewTab>('axial');
    const [slices, setSlices] = useState<SliceState | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);

    // Identity-stable handle — re-parse only when the buffer changes.
    const dataKey = useMemo(() => Symbol(), [arrayBuffer]);

    // Effect 1: build the pipeline once, dispose once. The wheel
    // callback uses functional setState so it never closes over a
    // stale `slices` value.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const pipeline = createPipeline(container, (axis, delta) => {
            setSlices((prev) =>
                prev
                    ? {
                          ...prev,
                          index: {
                              ...prev.index,
                              [axis]: clamp(prev.index[axis] + delta, 0, prev.count[axis] - 1),
                          },
                      }
                    : prev
            );
        });
        pipelineRef.current = pipeline;
        const resizeObserver = new ResizeObserver(() => pipeline.resize());
        resizeObserver.observe(container);
        return () => {
            resizeObserver.disconnect();
            try {
                pipeline.dispose();
            } catch (err) {
                logError(errorIds.VIEWER_DISPOSE_FAILED, 'pipeline dispose failed', {
                    viewer: 'multi-pane',
                    phase: 'unmount',
                    error: err,
                });
            }
            pipelineRef.current = null;
        };
    }, []);

    // Effect 2: parse the MHA on dataset change and push the image
    // data into the persistent pipeline.
    useEffect(() => {
        let cancelled = false;
        setParseError(null);
        (async () => {
            let imageData: vtkImageData;
            try {
                // parseMha runs itk-wasm with noCopy:true and detaches
                // the input ArrayBuffer; hand it a private slice so the
                // cached buffer survives.
                imageData = await parseMha(arrayBuffer.slice(0));
            } catch (err) {
                if (!cancelled) {
                    logError(errorIds.VIEWER_PREVIEW_PARSE_FAILED, 'Failed to parse preview MHA', {
                        viewer: 'multi-pane',
                        error: err,
                    });
                    setParseError(err instanceof Error ? err.message : 'Failed to parse preview');
                }
                return;
            }
            if (cancelled || !pipelineRef.current) {
                imageData.delete();
                return;
            }
            const { dims } = pipelineRef.current.setImageData(imageData);
            setSlices({
                count: { I: dims[0], J: dims[1], K: dims[2] },
                index: {
                    I: Math.floor(dims[0] / 2),
                    J: Math.floor(dims[1] / 2),
                    K: Math.floor(dims[2] / 2),
                },
            });
        })();
        return () => {
            cancelled = true;
        };
    }, [dataKey]);

    // Effect 3: tab switch — swap actor + interactor style + camera.
    useEffect(() => {
        pipelineRef.current?.showTab(activeTab);
    }, [activeTab]);

    // Effect 4: push slider- and wheel-driven slice index into the
    // active mapper.
    useEffect(() => {
        if (!slices || activeTab === '3d') return;
        const axis = AXIS_FOR_TAB[activeTab];
        pipelineRef.current?.setSliceIndex(axis, slices.index[axis]);
    }, [slices, activeTab]);

    const activeAxis: SliceAxis | null = activeTab === '3d' ? null : AXIS_FOR_TAB[activeTab];
    const activeSlice =
        slices && activeAxis ? { index: slices.index[activeAxis], total: slices.count[activeAxis] } : null;

    return (
        <div className="flex h-full w-full flex-col">
            {/* Tab bar */}
            <div className="flex items-center gap-1 border-b px-2">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={cn(
                            'border-b-2 px-3 py-2 text-xs font-medium transition-colors',
                            activeTab === tab.id
                                ? 'border-primary text-foreground'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                        )}
                        aria-pressed={activeTab === tab.id}
                    >
                        {tab.label}
                    </button>
                ))}
                <div className="flex-1" />
            </div>

            {/* Render area */}
            <div className="relative min-h-0 flex-1 overflow-hidden bg-black">
                <div ref={containerRef} className="h-full w-full" aria-label={`${activeTab} preview`} />
                {parseError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/80 px-4 text-center text-xs text-amber-200">
                        <AlertCircle className="h-5 w-5 text-amber-400" />
                        <p className="font-medium">Preview failed to load</p>
                        <p className="text-amber-200/70">{parseError}</p>
                    </div>
                )}
                {/* Top-right HUD: Patient Name + Patient ID & study date */}
                {(() => {
                    const name = formatDicomName(dataset.patientName);
                    const studyDate = formatDicomDate(dataset.studyDate);
                    const idLine = [dataset.patientId, studyDate].filter(Boolean).join(' - ');
                    if (!name && !idLine) return null;
                    return (
                        <div className="absolute top-1.5 right-1.5 rounded-sm font-mono text-right text-[10px] uppercase text-white/70 drop-shadow">
                            {name && <div className="text-white">{name}</div>}
                            {idLine && <div>{idLine}</div>}
                        </div>
                    );
                })()}

                {/* Bottom-right HUD: Decimated volume warning */}
                <div className="absolute bottom-1.5 right-1.5 rounded-sm text-amber-600 font-mono font-semibold text-[10px] uppercase tracking-wide">
                    Decimated preview - Not for diagnostic
                </div>
                {/* Bottom-left HUD: tab name + slice / total (or VR on 3D). */}
                <div className="pointer-events-none absolute bottom-1.5 left-1.5 font-mono text-[10px] uppercase text-white/80 drop-shadow">
                    {activeTab === '3d'
                        ? '3D · VR'
                        : activeSlice
                          ? `${activeTab} · ${activeSlice.index + 1} / ${activeSlice.total}`
                          : activeTab}
                </div>
                {/* Slice slider — hidden on 3D. */}
                {activeAxis && activeSlice && activeSlice.total > 1 && (
                    <div className="absolute w-full bottom-6.5 px-2">
                        <Slider
                            orientation="horizontal"
                            className={cn(
                                'flex-1 min-h-0 drop-shadow',
                                '**:data-[slot=slider-range]:bg-transparent',
                                '**:data-[slot=slider-track]:w-1',
                                '**:data-[slot=slider-thumb]:size-3'
                            )}
                            min={0}
                            max={activeSlice.total - 1}
                            value={[activeSlice.index]}
                            onValueChange={(values: number[]) => {
                                setSlices((prev) =>
                                    prev
                                        ? {
                                              ...prev,
                                              index: {
                                                  ...prev.index,
                                                  [activeAxis]: values[0],
                                              },
                                          }
                                        : prev
                                );
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Persistent VTK pipeline
// ============================================================================

interface Pipeline {
    /**
     * Wire `img` into all mappers and re-fit the active camera. The
     * pipeline takes ownership of `img` and deletes the previous one.
     */
    setImageData: (img: vtkImageData) => { dims: [number, number, number] };
    /** Swap actor, interactor style, and camera for the given tab. */
    showTab: (tab: PreviewTab) => void;
    /** Push a new slice index into the mapper for `axis`. */
    setSliceIndex: (axis: SliceAxis, idx: number) => void;
    /** React to container resize: update view size + re-fit camera. */
    resize: () => void;
    /** Tear down every VTK object owned by the pipeline. Call once. */
    dispose: () => void;
}

function createPipeline(
    container: HTMLElement,
    onWheelSlice: (axis: SliceAxis, delta: -1 | 1) => void
): Pipeline {
    // ---- Render window + view + interactor -----------------------
    const renderWindow = vtkRenderWindow.newInstance();
    const renderer = vtkRenderer.newInstance();
    renderer.setBackground(0, 0, 0);
    renderWindow.addRenderer(renderer);

    const view = vtkOpenGLRenderWindow.newInstance();
    renderWindow.addView(view);
    view.setContainer(container);
    applyViewSize(view, container);
    const canvas = view.getCanvas();
    if (canvas) canvas.style.height = '100%';

    const interactor = vtkRenderWindowInteractor.newInstance();
    interactor.setView(view);
    interactor.initialize();
    interactor.setContainer(container);

    // ---- Interactor styles (one of each, reused on every showTab) -
    const styleImage = vtkInteractorStyleImage.newInstance();
    const styleTrackball = vtkInteractorStyleTrackballCamera.newInstance();

    // ---- Slice pipeline (3 mappers + 3 actors) -------------------
    const sliceAxes: SliceAxis[] = ['I', 'J', 'K'];
    const sliceMappers = {} as Record<SliceAxis, vtkImageMapper>;
    const sliceActors = {} as Record<SliceAxis, vtkImageSlice>;
    sliceAxes.forEach((axis) => {
        const mapper = vtkImageMapper.newInstance();
        mapper.setSliceAtFocalPoint(false);
        const actor = vtkImageSlice.newInstance();
        actor.setMapper(mapper);
        const property = actor.getProperty();
        // CT-friendly initial window/level (soft-tissue range).
        property.setColorWindow(400);
        property.setColorLevel(40);
        sliceMappers[axis] = mapper;
        sliceActors[axis] = actor;
    });

    // ---- Volume pipeline -----------------------------------------
    const volumeMapper = vtkVolumeMapper.newInstance();
    const volumeActor = vtkVolume.newInstance();
    volumeActor.setMapper(volumeMapper);

    const opacity = vtkPiecewiseFunction.newInstance();
    opacity.addPoint(125, 0.0);
    opacity.addPoint(395, 0.8);

    const color = vtkColorTransferFunction.newInstance();
    color.addRGBPoint(32, 0.0, 0.0, 0.0);
    color.addRGBPoint(125, 0.0, 0.0, 0.0);
    color.addRGBPoint(224, 1.0, 0.0823529, 0.105882);
    color.addRGBPoint(280, 1.0, 0.690196, 0.0352941);
    color.addRGBPoint(337, 1.0, 0.945098, 0.152941);
    color.addRGBPoint(395, 1.0, 1.0, 1.0);

    const volumeProperty = volumeActor.getProperty();
    volumeProperty.setRGBTransferFunction(0, color);
    volumeProperty.setScalarOpacity(0, opacity);
    volumeProperty.setShade(true);
    volumeProperty.setAmbient(0.1);
    volumeProperty.setDiffuse(0.9);
    volumeProperty.setSpecular(0.2);
    volumeProperty.setSpecularPower(10);
    volumeProperty.setInterpolationTypeToLinear();

    // ---- Mutable state owned by the pipeline ---------------------
    let currentTab: PreviewTab | null = null;
    let currentActor: vtkImageSlice | vtkVolume | null = null;
    let imageData: vtkImageData | null = null;

    // ---- Wheel listener (always attached; branches on currentTab)
    const onWheel = (ev: WheelEvent) => {
        if (currentTab === null || currentTab === '3d') return;
        ev.preventDefault();
        onWheelSlice(AXIS_FOR_TAB[currentTab], ev.deltaY > 0 ? 1 : -1);
    };
    container.addEventListener('wheel', onWheel, { passive: false });

    // showTab / resize / setSliceIndex can all fire before
    // setImageData on first mount (the parseMha is async, but Effect
    // 3 and the ResizeObserver run synchronously). Rendering without
    // input makes vtkImageMapper log "No input!" — gate every render
    // outside setImageData on having data wired in.
    const renderIfReady = () => {
        if (imageData) renderWindow.render();
    };

    // ---- Camera fitting ------------------------------------------
    // Always fully reconfigures the camera — never assumes previous
    // state, since the same camera is reused across tab switches.
    const fitCameraForTab = (tab: PreviewTab) => {
        if (!imageData) return;
        const camera = renderer.getActiveCamera();
        const bounds = imageData.getBounds() as [number, number, number, number, number, number];
        const cx = (bounds[0] + bounds[1]) / 2;
        const cy = (bounds[2] + bounds[3]) / 2;
        const cz = (bounds[4] + bounds[5]) / 2;
        const extentX = bounds[1] - bounds[0];
        const extentY = bounds[3] - bounds[2];
        const extentZ = bounds[5] - bounds[4];
        const { width, height } = container.getBoundingClientRect();
        const aspectRatio = width > 0 && height > 0 ? width / height : 1;

        camera.setFocalPoint(cx, cy, cz);

        if (tab === '3d') {
            camera.setParallelProjection(false);
            const viewAngle = camera.getViewAngle() || 30;
            const halfAngleRad = ((viewAngle / 2) * Math.PI) / 180;
            const horizontalHalfAngle = Math.atan(Math.tan(halfAngleRad) * aspectRatio);
            const distFromX = extentX / 2 / Math.tan(horizontalHalfAngle);
            const distFromZ = extentZ / 2 / Math.tan(halfAngleRad);
            const fittingDist = Math.max(distFromX, distFromZ);
            const clearanceDist = extentY / 2;
            const distance = Math.max(fittingDist, clearanceDist) * 1.1;
            camera.setPosition(cx, cy - distance, cz);
            camera.setViewUp(0, 0, 1);
            renderer.updateLightsGeometryToFollowCamera();
        } else {
            camera.setParallelProjection(true);
            const axis = AXIS_FOR_TAB[tab];
            if (axis === 'K') {
                const dist = Math.max(extentX, extentY);
                camera.setPosition(cx, cy, cz - dist);
                camera.setViewUp(0, -1, 0);
                camera.setParallelScale(Math.max(extentY / 2, extentX / 2 / aspectRatio));
            } else if (axis === 'J') {
                const dist = Math.max(extentX, extentZ);
                camera.setPosition(cx, cy - dist, cz);
                camera.setViewUp(0, 0, 1);
                camera.setParallelScale(Math.max(extentZ / 2, extentX / 2 / aspectRatio));
            } else {
                const dist = Math.max(extentY, extentZ);
                camera.setPosition(cx - dist, cy, cz);
                camera.setViewUp(0, 0, 1);
                camera.setParallelScale(Math.max(extentZ / 2, extentY / 2 / aspectRatio));
            }
        }
        renderer.resetCameraClippingRange();
    };

    return {
        setImageData(img) {
            if (imageData) imageData.delete();
            imageData = img;

            const dims = img.getDimensions() as [number, number, number];
            sliceAxes.forEach((axis) => {
                sliceMappers[axis].setInputData(img);
                const dim = axisDim(dims, axis);
                setMapperSlice(sliceMappers[axis], axis, Math.floor(dim / 2));
            });
            volumeMapper.setInputData(img);

            if (currentTab !== null) fitCameraForTab(currentTab);
            renderWindow.render();

            return { dims };
        },

        showTab(tab) {
            if (currentActor) renderer.removeViewProp(currentActor);
            if (tab === '3d') {
                renderer.addVolume(volumeActor);
                currentActor = volumeActor;
                interactor.setInteractorStyle(styleTrackball);
            } else {
                const actor = sliceActors[AXIS_FOR_TAB[tab]];
                renderer.addActor(actor);
                currentActor = actor;
                interactor.setInteractorStyle(styleImage);
            }
            currentTab = tab;
            fitCameraForTab(tab);
            renderIfReady();
        },

        setSliceIndex(axis, idx) {
            setMapperSlice(sliceMappers[axis], axis, idx);
            renderIfReady();
        },

        resize() {
            applyViewSize(view, container);
            if (currentTab) fitCameraForTab(currentTab);
            renderIfReady();
        },

        dispose() {
            // Strict reverse-of-construction order. With a single
            // render window (no child render windows registered), the
            // ViewNode cascade on view.delete() walks an empty
            // children array, so there's no double-delete hazard.
            container.removeEventListener('wheel', onWheel);

            interactor.setContainer(null);
            interactor.delete();

            styleImage.delete();
            styleTrackball.delete();

            sliceAxes.forEach((axis) => {
                renderer.removeViewProp(sliceActors[axis]);
                sliceActors[axis].delete();
                sliceMappers[axis].delete();
            });
            renderer.removeViewProp(volumeActor);
            volumeActor.delete();
            volumeMapper.delete();
            opacity.delete();
            color.delete();

            renderWindow.removeRenderer(renderer);
            renderer.delete();

            view.setContainer(null);
            renderWindow.removeView(view);
            view.delete();
            renderWindow.delete();

            if (imageData) imageData.delete();
            imageData = null;
            currentActor = null;
            currentTab = null;
        },
    };
}

function applyViewSize(view: vtkOpenGLRenderWindow, container: HTMLElement): void {
    const { width, height } = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    view.setSize(Math.max(Math.floor(width * dpr), 1), Math.max(Math.floor(height * dpr), 1));
}

function setMapperSlice(mapper: vtkImageMapper, axis: SliceAxis, index: number): void {
    if (axis === 'K') mapper.setKSlice(index);
    else if (axis === 'J') mapper.setJSlice(index);
    else mapper.setISlice(index);
}

function axisDim(dims: [number, number, number], axis: SliceAxis): number {
    return axis === 'I' ? dims[0] : axis === 'J' ? dims[1] : dims[2];
}

function clamp(value: number, min: number, max: number): number {
    if (Number.isNaN(value)) return min;
    return Math.max(min, Math.min(max, value));
}
